import { ApiError, ContractMismatchError, TransportError } from "@agentry/api-client";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button, Callout, LinkButton } from "./primitives";

/**
 * Loading / Empty / Forbidden / NotFound / Error の共通表示。
 * 「原因別に、次にやることが1つ書いてある」ことを最低条件にする。
 */

export function LoadingState({ label = "読み込み中です" }: { label?: string }) {
	return (
		<div
			role="status"
			aria-live="polite"
			className="flex min-h-28 items-center justify-center gap-3 rounded-card border border-ink-200 bg-white px-5 py-8 text-[14px] font-medium text-ink-600 shadow-state"
		>
			<span className="size-4 animate-spin rounded-full border-2 border-ink-300 border-t-brand-600" />
			{label}
		</div>
	);
}

export function EmptyState({
	title,
	description,
	action,
}: {
	title: string;
	description?: string;
	action?: ReactNode;
}) {
	return (
		<div className="rounded-card border border-dashed border-ink-300 bg-ink-50/70 px-6 py-12 text-center shadow-state">
			<p className="text-[15px] font-bold text-ink-800">{title}</p>
			{description && (
				<p className="mx-auto mt-1 max-w-md text-[13px] text-ink-500">{description}</p>
			)}
			{action && <div className="mt-4 flex justify-center gap-2">{action}</div>}
		</div>
	);
}

export function ForbiddenState({ detail }: { detail?: string }) {
	return (
		<div
			data-testid="forbidden"
			className="rounded-card border border-warn-500/40 bg-warn-100 px-6 py-10 text-center shadow-state"
		>
			<p className="text-[15px] font-bold text-warn-600">この画面を表示する権限がありません</p>
			<p className="mx-auto mt-1 max-w-md text-[13px] text-ink-700">
				{detail ?? "必要な権限が割り当てられていません。組織の管理者へ利用申請してください。"}
			</p>
			<div className="mt-4 flex justify-center gap-2">
				<LinkButton to="/app/workspace" variant="secondary" size="sm">
					ホームへ戻る
				</LinkButton>
			</div>
		</div>
	);
}

export function NotFoundState({ detail }: { detail?: string }) {
	return (
		<div
			data-testid="not-found"
			className="rounded-lg border border-ink-300 bg-white px-6 py-10 text-center"
		>
			<p className="text-[15px] font-bold text-ink-800">ページまたはデータが見つかりません</p>
			<p className="mx-auto mt-1 max-w-md text-[13px] text-ink-500">
				{detail ?? "URLが変更されたか、アクセスできる範囲にありません。"}
			</p>
			<div className="mt-4 flex justify-center gap-2">
				<LinkButton to="/" variant="secondary" size="sm">
					トップへ戻る
				</LinkButton>
			</div>
		</div>
	);
}

/**
 * 「見つからない／見せない」判定。
 * Message文字列ではなくHTTP status と reason で判定する。
 * Tenant越境は存在を漏らさないため 404 として扱う（design §24.9）。
 */
export function isNotFound(error: unknown): boolean {
	if (!(error instanceof ApiError)) return false;
	return (
		error.status === 404 ||
		error.reason === "TENANT_MISMATCH" ||
		error.reason === "ARTIFACT_NOT_FOUND"
	);
}

/** Error本文ではなく`reason`から復旧手段を決める（Message文字列で分岐しない）。 */
export type Recovery = {
	title: string;
	hint: string;
	retryable: boolean;
	actionTo?: string;
	actionLabel?: string;
};

export function recoveryFor(error: unknown): Recovery {
	if (error instanceof ContractMismatchError) {
		return {
			title: "サービスを更新しています",
			hint: "安全に接続するため、新しいバージョンへの切り替えを待っています。しばらくしてからもう一度お試しください。",
			retryable: false,
		};
	}
	if (error instanceof TransportError) {
		return {
			title: "サービスに接続できませんでした",
			hint: "通信状態を確認して、もう一度お試しください。入力済みの内容はそのまま保持されます。",
			retryable: true,
		};
	}
	if (error instanceof ApiError) {
		switch (error.reason) {
			case "TENANT_MISMATCH":
				return {
					title: "このデータは見つかりません",
					hint: "所属していない組織のデータにはアクセスできません。",
					retryable: false,
					actionTo: "/app/workspace",
					actionLabel: "ホームへ",
				};
			case "ROLE_NOT_PERMITTED":
				return {
					title: "権限がありません",
					hint: "組織の管理者へ利用権限の付与を依頼してください。",
					retryable: false,
				};
			case "TOKEN_EXPIRED":
			case "TOKEN_INVALID":
				return {
					title: "ログインの有効期限が切れました",
					hint: "もう一度ログインすると、作業中の内容から再開できます。",
					retryable: false,
					actionTo: "/login",
					actionLabel: "ログインする",
				};
			case "STATE_VERSION_CONFLICT":
				return {
					title: "他の操作で状態が更新されています",
					hint: "最新の状態を取得してからもう一度実行してください。",
					retryable: true,
				};
			case "EXECUTION_TERMINAL":
				return {
					title: "この作業はすでに終了しています",
					hint: "再開はできません。やり直す場合は新しい実行として開始してください。",
					retryable: false,
				};
			case "IDEMPOTENCY_KEY_MISMATCH":
				return {
					title: "同じ操作IDで異なる内容が送信されました",
					hint: "画面を再読み込みしてから、もう一度送信してください。",
					retryable: false,
				};
			case "CHECKSUM_MISMATCH":
				return {
					title: "成果物の内容が検証できませんでした",
					hint: "壊れている可能性があるため表示しません。もう一度取得してください。",
					retryable: true,
				};
			case "SECRET_LITERAL_REJECTED":
				return {
					title: "秘密情報そのものは保存できません",
					hint: "env: / vault: 形式の参照を入力してください。API KeyやTokenの本文は受け付けません。",
					retryable: false,
				};
			case "PROVIDER_POLICY_DENIED":
			case "PROVIDER_POLICY_UNVERIFIED":
			case "ASSIGNMENT_DENIED":
				return {
					title: "この設定は許可されていません",
					hint: "上位のポリシーで禁止されています。組織の管理者に確認してください。",
					retryable: false,
				};
			case "AUTH_PROFILE_EXPIRED":
			case "NO_AUTH_CANDIDATE":
				return {
					title: "利用できるAI接続がありません",
					hint: "AI接続設定から再認証するか、共有接続の利用許可を依頼してください。",
					retryable: false,
					actionTo: "/app/settings/credentials",
					actionLabel: "AI接続設定へ",
				};
			case "QUOTA_EXHAUSTED":
				return {
					title: "利用上限に達しました",
					hint: "予算・上限の見直しを組織の管理者へ依頼してください。",
					retryable: false,
				};
			case "ARTIFACT_NOT_FOUND":
				return {
					title: "成果物が見つかりません",
					hint: "削除されたか、保管期限を過ぎている可能性があります。",
					retryable: false,
				};
			case "GITEA_NOT_CONFIGURED":
				return {
					title: "Gitea 未接続",
					hint: "コード用リポジトリを用意できません。Zerovia 側の Gitea 設定を確認してください。",
					retryable: true,
				};
			case "GITEA_API_ERROR":
			case "GITEA_CREATE_NOT_SUPPORTED":
				return {
					title: "リポジトリ準備に失敗しました",
					hint: "もう一度「再試行」してください。解消しない場合は管理者へ連絡してください。",
					retryable: true,
				};
			default:
				break;
		}
		if (error.status === 404) {
			return { title: "見つかりません", hint: "URLをご確認ください。", retryable: false };
		}
		return {
			title: "処理を完了できませんでした",
			hint: "しばらく待ってからもう一度お試しください。",
			retryable: true,
		};
	}
	return {
		title: "予期しないエラーが発生しました",
		hint: "もう一度お試しください。解消しない場合はサポートへご連絡ください。",
		retryable: true,
	};
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
	const recovery = recoveryFor(error);
	return (
		<div
			data-testid="error-state"
			role="alert"
			className="rounded-card border border-danger-500/35 bg-white px-5 py-7 shadow-state"
		>
			<p className="text-[15px] font-bold text-danger-600">{recovery.title}</p>
			<p className="mt-1 text-[13px] text-ink-700">{recovery.hint}</p>
			<div className="mt-4 flex flex-wrap gap-2">
				{recovery.retryable && onRetry && (
					<Button size="sm" onClick={onRetry}>
						もう一度試す
					</Button>
				)}
				{recovery.actionTo && (
					<LinkButton to={recovery.actionTo} variant="secondary" size="sm">
						{recovery.actionLabel ?? "移動する"}
					</LinkButton>
				)}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Error Boundary
// ---------------------------------------------------------------------------

type BoundaryState = { error: Error | null };

export class AppErrorBoundary extends Component<{ children: ReactNode }, BoundaryState> {
	state: BoundaryState = { error: null };

	static getDerivedStateFromError(error: Error): BoundaryState {
		return { error };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		// 握り潰さず、開発時に原因を追える形で残す。Secretは載せない。
		console.error("[Agentry] render error", error, info.componentStack);
	}

	render(): ReactNode {
		if (!this.state.error) return this.props.children;
		return (
			<div className="mx-auto max-w-2xl px-4 py-16">
				<Callout tone="danger" title="画面の表示中に問題が発生しました">
					<p className="mt-1">
						作業内容は保存されている場合があります。再読み込みしても直らないときは、ホームへ戻ってから操作をやり直してください。
					</p>
					<p className="mt-2 font-mono text-[11px] text-ink-500">{this.state.error.message}</p>
					<div className="mt-3 flex gap-2">
						<Button size="sm" onClick={() => this.setState({ error: null })}>
							この画面を再描画する
						</Button>
						<LinkButton to="/" variant="secondary" size="sm">
							トップへ戻る
						</LinkButton>
					</div>
				</Callout>
			</div>
		);
	}
}
